// public/v3/src/lib/pin-billing.js — Principal Illness Navigation (G0023/G0024).
//
// PIN time is its own activity-log family, distinct from CCM, BHI, and CPM.
// This module contains pure CommonJS helpers so Jest and the billing engines
// can consume one deterministic implementation.

const { entryProgramFamily } = require('./bhi-billing');

const NAV_BASE_CODE = 'G0023';
const NAV_ADDON_CODE = 'G0024';
const NAV_BASE_TARGET_MIN = 60;
const NAV_ADDON_UNIT_MIN = 30;
// Deliberate conservative guard: CMS publishes no G0024 unit cap.
const NAV_ADDON_MAX_UNITS = 4;

function _raw(patient) {
  return patient && patient._raw ? patient._raw : (patient || {});
}

function isPinEnrolledPatient(patient) {
  return ((_raw(patient).billing || {}).navProgram) === NAV_BASE_CODE;
}

// True when the persisted PIN record can support billing G0023: dedicated
// consent signed, a non-empty qualifying serious high-risk condition, and an
// initiating visit recorded. pinGatesForMonth checks the same facts (plus
// enrollment + minutes); this boolean drives the Onboard "PIN record
// incomplete" chip so the UI and the gate never disagree on completeness.
function isPinRecordComplete(patient) {
  const pin = _raw(patient).pin || {};
  return !!(pin.consent && pin.consent.signed === true
    && typeof pin.qualifyingCondition === 'string' && pin.qualifyingCondition.trim()
    && pin.initiatingVisit && pin.initiatingVisit.date);
}

// Build the persisted PIN record (raw.pin) written at enrollment / backfill.
// PIN minutes are auxiliary-personnel time, so there is no designated-provider
// counting anchor and this record deliberately has no billingProvider key.
function buildPinEnrollmentRecord(opts) {
  const o = opts || {};
  const prov = o.provider || {};
  const practitionerId = prov.id != null && prov.id !== '' ? String(prov.id) : '';
  const rec = {
    consent: {
      signed: true, date: o.consentDate || '', mode: o.mode || 'verbal', disclosures: true,
    },
    qualifyingCondition: String(o.qualifyingCondition || '').trim(),
    navigatorTraining: { attested: true, date: o.consentDate || '' },
  };
  if (o.initiatingVisitDate) {
    rec.initiatingVisit = {
      date: o.initiatingVisitDate,
      practitionerName: prov.name || '',
      practitionerId,
    };
  }
  return rec;
}

// PIN-family minutes for the month (G0023/G0024-tagged entries only).
function pinMonthlyMinutes(patient, monthStr) {
  const log = Array.isArray(_raw(patient).activityLog) ? _raw(patient).activityLog : [];
  return log
    .filter((e) => e && !e.isConsolidatedNote && (e.date || '').startsWith(monthStr || '') && entryProgramFamily(e) === 'nav')
    .reduce((acc, e) => acc + (parseInt(e.duration, 10) || 0), 0);
}

// Month gates for billing G0023. Blockers are operator-readable strings.
function pinGatesForMonth(patient, monthStr) {
  const raw = _raw(patient);
  const blockers = [];
  if (!isPinEnrolledPatient(raw)) blockers.push('not enrolled in principal illness navigation (billing.navProgram)');
  const pinRec = raw.pin || {};
  if (!(pinRec.consent && pinRec.consent.signed === true)) blockers.push('PIN consent record incomplete');
  if (!(typeof pinRec.qualifyingCondition === 'string' && pinRec.qualifyingCondition.trim())) {
    blockers.push('qualifying serious high-risk condition not recorded');
  }
  if (!(pinRec.initiatingVisit && pinRec.initiatingVisit.date)) blockers.push('initiating visit not recorded');
  const minutes = pinMonthlyMinutes(raw, monthStr);
  if (minutes < NAV_BASE_TARGET_MIN) blockers.push('PIN time below 60 min (' + minutes + 'm logged)');
  return { ok: blockers.length === 0, blockers, minutes };
}

// CPT lines for a gates-passing month: G0023 plus one G0024 per additional
// full 30 minutes, capped at the deliberate conservative guard above.
function resolvePinCodes(patient, monthStr, rateDescs) {
  const descs = rateDescs || {};
  const minutes = pinMonthlyMinutes(patient, monthStr);
  const codes = [{
    code: NAV_BASE_CODE,
    unit: 1,
    desc: descs[NAV_BASE_CODE] || 'Principal illness navigation, first 60 min/month',
  }];
  const units = Math.min(Math.floor(Math.max(0, minutes - NAV_BASE_TARGET_MIN) / NAV_ADDON_UNIT_MIN), NAV_ADDON_MAX_UNITS);
  for (let u = 1; u <= units; u++) {
    codes.push({
      code: NAV_ADDON_CODE,
      unit: u,
      desc: descs[NAV_ADDON_CODE] || 'Principal illness navigation, each addl 30 min',
    });
  }
  return codes;
}

// Deterministic PIN attestation block. Persisted record details are emitted
// only when chart-backed; the auxiliary-personnel statement is an attestation
// and intentionally does not name an individual navigator.
function buildPinAttestationLines(patient, monthStr, staffList, formatEntryStaff) {
  const raw = _raw(patient);
  const lines = [];
  lines.push('PRINCIPAL ILLNESS NAVIGATION (G0023):');
  const pinRec = raw.pin || {};
  if (typeof pinRec.qualifyingCondition === 'string' && pinRec.qualifyingCondition.trim()) {
    lines.push('- Qualifying condition: ' + pinRec.qualifyingCondition.trim()
      + ' — serious, high-risk, expected to last >=3 months (attested at enrollment).');
  }
  const iv = pinRec.initiatingVisit || {};
  if (iv.date) {
    lines.push('- Initiating visit: ' + iv.date + (iv.practitionerName ? ' by ' + iv.practitionerName : '') + '.');
  }
  const consent = pinRec.consent || {};
  if (consent.signed === true && consent.date) {
    lines.push('- PIN consent' + (consent.mode ? ' (' + consent.mode + ')' : '') + ' obtained ' + consent.date + '.');
  }
  lines.push('- Navigation time this month: ' + pinMonthlyMinutes(raw, monthStr)
    + ' minutes (itemized below), distinct from any CCM/BHI/CPM time.');
  lines.push('- Navigation furnished by trained auxiliary personnel under the general supervision of the billing practitioner; training documentation attested at enrollment (see audit log).');
  const entries = (Array.isArray(raw.activityLog) ? raw.activityLog : [])
    .filter((e) => e && !e.isConsolidatedNote && (e.date || '').startsWith(monthStr || '') && entryProgramFamily(e) === 'nav')
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  if (entries.length) {
    lines.push('Itemized navigation time log:');
    for (const e of entries) {
      const dur = parseInt(e.duration, 10) || 0;
      const type = e.type || e.label || 'Principal illness navigation';
      const staffStr = typeof formatEntryStaff === 'function' ? formatEntryStaff(e, staffList) : (e.staff || '');
      const note = (e.notes || e.desc || '').trim();
      lines.push('- ' + (staffStr ? staffStr + ' — ' : '') + dur + ' min — ' + type + (note ? ' — ' + note : ''));
    }
  }
  return lines;
}

module.exports = {
  NAV_BASE_CODE,
  NAV_ADDON_CODE,
  NAV_BASE_TARGET_MIN,
  NAV_ADDON_UNIT_MIN,
  NAV_ADDON_MAX_UNITS,
  isPinEnrolledPatient,
  isPinRecordComplete,
  buildPinEnrollmentRecord,
  pinMonthlyMinutes,
  pinGatesForMonth,
  resolvePinCodes,
  buildPinAttestationLines,
};
